import { Button } from '@/components/ui/button'
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from '@/components/ui/dialog'
import { isSafariBrowser } from '@/lib/browser'
import { cn } from '@/lib/utils'
import { resourceService } from '@/services/resource.service'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { useQuery } from '@tanstack/react-query'
import { Download, FileText, Loader2, X } from 'lucide-react'
import { type ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import pdfWorkerSrc from 'react-pdf/node_modules/pdfjs-dist/build/pdf.worker.min.mjs?url'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc

const PDF_WASM_URL = import.meta.env.DEV
  ? '/node_modules/react-pdf/node_modules/pdfjs-dist/wasm/'
  : 'https://unpkg.com/pdfjs-dist@5.4.296/wasm/'

/** Centers loading UI; full-window cover is rendered on the dialog shell. */
function PdfLoadingCenter({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center px-3 py-8">
      {children}
    </div>
  )
}

/** Horizontal bar loader — editorial corporate style, navy/light-gray palette. */
function PdfLinearLoader({
  percent,
  indeterminate,
}: {
  percent: number | null
  indeterminate: boolean
}) {
  const clamped = percent != null ? Math.min(100, Math.max(0, percent)) : 0
  const subLabel = indeterminate ? 'Fetching file...' : `${clamped}% loaded`

  return (
    <output
      className="flex w-64 flex-col items-center gap-4 border-0 bg-transparent"
      aria-live="polite"
      aria-busy="true"
    >
      <FileText className="h-5 w-5 text-primary opacity-50" aria-hidden />
      <div className="flex flex-col items-center gap-1">
        <p className="font-sans text-sm font-medium tracking-tight text-primary">
          Preparing document
        </p>
        <p className="font-sans text-xs text-muted-foreground">{subLabel}</p>
      </div>
      <div className="h-1 w-full overflow-hidden rounded-full bg-secondary">
        {indeterminate ? (
          <div className="h-full w-1/3 rounded-full bg-primary motion-safe:animate-pdf-progress-indeterminate" />
        ) : (
          <div
            className="h-full rounded-full bg-primary motion-safe:transition-[width] motion-safe:duration-300 motion-safe:ease-out"
            style={{ width: `${clamped}%` }}
          />
        )}
      </div>
    </output>
  )
}

type ResourcePdfViewerDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  resourceId: number | null
  title: string
}

export function ResourcePdfViewerDialog({
  open,
  onOpenChange,
  resourceId,
  title,
}: ResourcePdfViewerDialogProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [pageWidth, setPageWidth] = useState(800)
  const [numPages, setNumPages] = useState(0)
  const [pdfRenderError, setPdfRenderError] = useState(false)
  const [pdfData, setPdfData] = useState<{ data: ArrayBuffer } | null>(null)
  const [isFetchingPdfBytes, setIsFetchingPdfBytes] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const pdfOptions = useMemo(
    () => ({
      wasmUrl: PDF_WASM_URL,
      ...(isSafariBrowser()
        ? {
            isImageDecoderSupported: false,
            isOffscreenCanvasSupported: false,
          }
        : {}),
    }),
    [],
  )

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['resources', 'download-url', resourceId],
    queryFn: () => resourceService.getDownloadUrl(resourceId as number),
    enabled: open && resourceId != null,
    staleTime: 0,
  })

  // Fetch PDF as ArrayBuffer and pass directly to react-pdf.
  // This avoids cross-origin issues with the pdf.js worker.
  useEffect(() => {
    if (!data?.download_url) {
      setPdfData(null)
      return
    }
    let cancelled = false
    setIsFetchingPdfBytes(true)
    fetch(data.download_url)
      .then((res) => res.arrayBuffer())
      .then((buf) => {
        if (!cancelled) {
          setIsFetchingPdfBytes(false)
          setPdfData({ data: buf })
        }
      })
      .catch((err) => {
        console.error('PDF fetch error:', err)
        if (!cancelled) {
          setIsFetchingPdfBytes(false)
          setPdfRenderError(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [data?.download_url])

  const pdfUrl = pdfData

  const isPdfBodyLoading =
    isLoading ||
    isFetching ||
    isFetchingPdfBytes ||
    (Boolean(pdfUrl) && !pdfRenderError && numPages === 0)

  useEffect(() => {
    if (!open) {
      setNumPages(0)
      setPdfRenderError(false)
      setPdfData(null)
      setIsFetchingPdfBytes(false)
    }
  }, [open])

  useEffect(() => {
    const el = scrollRef.current
    if (!el || !open) return

    const measure = () => {
      setPageWidth(Math.max(240, el.clientWidth))
    }

    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [open])

  // Re-measure when pages are about to render — by this point the dialog is
  // fully laid out and clientWidth is reliable (guards against portal timing).
  useLayoutEffect(() => {
    if (numPages > 0 && scrollRef.current) {
      setPageWidth(Math.max(240, scrollRef.current.clientWidth))
    }
  }, [numPages])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          className="fixed left-[50%] top-[50%] z-50 flex h-[min(90dvh,900px)] w-[min(100vw-1rem,56rem)] max-w-none translate-x-[-50%] translate-y-[-50%] flex-col gap-0 overflow-hidden border bg-background p-0 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg"
          aria-describedby={undefined}
        >
          {isPdfBodyLoading ? (
            <div className="pointer-events-none absolute inset-0 z-0 bg-muted/60" aria-hidden />
          ) : null}

          <div className="relative z-10 flex min-h-0 flex-1 flex-col">
            <div className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-background px-4">
              <DialogTitle className="min-w-0 flex-1 truncate pr-2 text-left text-base font-semibold leading-tight">
                {title}
              </DialogTitle>
              <div className="flex shrink-0 items-center gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  disabled={resourceId == null || isDownloading}
                  onClick={async () => {
                    if (resourceId != null) {
                      setIsDownloading(true)
                      try {
                        await resourceService.triggerResourceDownload(resourceId, title)
                      } finally {
                        setIsDownloading(false)
                      }
                    }
                  }}
                >
                  {isDownloading ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <Download className="h-4 w-4" aria-hidden />
                  )}
                  {isDownloading ? 'Downloading...' : 'Download'}
                </Button>
                <DialogPrimitive.Close
                  type="button"
                  className="rounded-md p-2 opacity-80 ring-offset-background transition-all hover:opacity-100 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" aria-hidden />
                </DialogPrimitive.Close>
              </div>
            </div>

            <div
              ref={scrollRef}
              className={cn(
                'min-h-0 flex-1 overflow-y-auto bg-muted/40',
                isPdfBodyLoading ? 'flex flex-col' : 'py-4',
              )}
            >
              {isLoading || isFetching || isFetchingPdfBytes ? (
                <PdfLoadingCenter>
                  <PdfLinearLoader percent={null} indeterminate />
                </PdfLoadingCenter>
              ) : isError ? (
                <div className="flex flex-col items-center gap-3 px-4 py-12 text-center text-sm text-muted-foreground">
                  <p>Could not load this PDF preview.</p>
                  <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>
                    Try again
                  </Button>
                </div>
              ) : pdfUrl ? (
                pdfRenderError ? (
                  <div className="flex flex-col items-center gap-3 px-4 py-12 text-center text-sm text-muted-foreground">
                    <p>Could not render this PDF (network or file access).</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setPdfRenderError(false)
                        void refetch()
                      }}
                    >
                      Try again
                    </Button>
                  </div>
                ) : (
                  <Document
                    file={pdfUrl}
                    options={pdfOptions}
                    loading={null}
                    onLoadSuccess={({ numPages: n }) => {
                      setPdfRenderError(false)
                      setNumPages(n)
                    }}
                    onLoadError={() => {
                      setNumPages(0)
                      setPdfRenderError(true)
                    }}
                  >
                    {numPages > 0
                      ? Array.from({ length: numPages }, (_, index) => index + 1).map(
                          (pageNumber) => (
                            <Page
                              key={`${resourceId}-${pageNumber}`}
                              pageNumber={pageNumber}
                              width={pageWidth}
                              className="block"
                              canvasBackground="white"
                              loading={null}
                              renderTextLayer
                              renderAnnotationLayer
                            />
                          ),
                        )
                      : null}
                  </Document>
                )
              ) : null}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}
